import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useWsEvent } from '@/auth/WsProvider';
import { useCan } from '@/auth/AuthContext';
import StudentFilterChip from './StudentFilterChip';

const PROVIDER_LABELS = {
  cash:            'Наличные',
  card_sbp:        'Карта/СБП',
  yookassa:        'ЮКасса',
  bank_transfer:   'Банковский перевод',
  onec_correction: 'Корректировка 1С',
  // legacy
  manual:          'Ручная',
  modulkassa:      'Модулькасса',
  terminal:        'Терминал',
};
const KIND_LABELS = {
  school_monthly:        'Школа · ежемесячно',
  school_admission_fee:  'Школа · вступительный',
  kg_monthly:            'Сад · ежемесячно',
  kg_admission_fee:      'Сад · вступительный',
  extra_lesson:          'Доп.занятие',
  extra_camp:            'Лагерь',
  manual:                'Произвольно',
  discount:              'Скидка',
  refund:                'Возврат',
};
const STATUS_LABELS = {
  draft:               { label: 'Черновик',     cls: 'pay-st pay-st--draft' },
  pending:             { label: 'Ожидает',      cls: 'pay-st pay-st--pending' },
  succeeded:           { label: 'Оплачен',      cls: 'pay-st pay-st--ok' },
  failed:              { label: 'Ошибка',       cls: 'pay-st pay-st--err' },
  canceled:            { label: 'Отменён',      cls: 'pay-st pay-st--mute' },
  refund_requested:    { label: 'Возврат…',     cls: 'pay-st pay-st--pending' },
  partially_refunded:  { label: 'Возвращ. ч.',  cls: 'pay-st pay-st--mute' },
  refunded:            { label: 'Возвращён',    cls: 'pay-st pay-st--mute' },
};

function fmtAmount(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}
function fmtDate(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 16);
}
// Короткий формат даты «дд.мм.гг» — для подписи договора в шапке платежа.
function fmtDateRuShort(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : iso;
}

/**
 * `nested` — модалка открыта поверх другого модального окна (например, в
 * карточке ученика). В этом режиме:
 *   • НЕ трогаем `document.body.style.overflow` — родитель уже залочил скролл,
 *     иначе при закрытии payment-модалки разлочим body, пока student-модалка
 *     ещё открыта.
 *   • НЕ слушаем глобальный ESC — родитель сам решает, какое окно закрывать
 *     первым (свой ESC-handler он делает условным от наличия nested-модалки).
 */
export default function PaymentDetailModal({ paymentId, canWrite, canDelete, onClose, nested = false }) {
  const can = useCan();
  const canDeleteCorrection = can('payments.corrections', 'delete');
  // Перенос платежа между учениками/договорами — отдельное право.
  const canReassign = can('payments.reassign', 'write');

  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  // Inline-формы вместо prompt/confirm
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState('manual');
  const [addName, setAddName] = useState('');
  const [addPrice, setAddPrice] = useState('');
  const [addQty,   setAddQty]   = useState('1');

  const [confirmOpen, setConfirmOpen] = useState(false);     // подтверждение «Оплачено»
  const [cancelOpen,  setCancelOpen]  = useState(false);     // форма причины отмены
  const [cancelReason, setCancelReason] = useState('');
  const [destroyOpen, setDestroyOpen] = useState(false);

  // Inline-форма «Сменить плательщика/договор» — для уже проведённых платежей.
  // Открывается под action-баром, грузит контекст (договоры) при выборе ученика.
  const [rebindOpen, setRebindOpen] = useState(false);
  const [rebindStudent, setRebindStudent] = useState(null); // { id, familyName, firstName, midName }
  const [rebindAgreementId, setRebindAgreementId] = useState('');
  const [rebindAgreements, setRebindAgreements] = useState([]);
  const [rebindLoading, setRebindLoading] = useState(false);

  useEffect(() => {
    if (nested) return; // см. JSDoc: ESC и body-lock — на стороне родителя.
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, busy, nested]);

  const reload = () => {
    setError(null);
    api.get('/api/payments/' + paymentId)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'));
  };
  useEffect(reload, [paymentId]);

  // WS: при оплате через ЮКассу/Модулькассу/корректировку статус прилетает
  // webhook'ом → Node бродкастит payment.updated → перезагружаем эту карточку.
  useWsEvent('payment.updated', (msg) => {
    if (msg && Number(msg.paymentId) === paymentId) reload();
  });

  const act = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); reload(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Не удалось'); }
    finally { setBusy(false); }
  };

  const onConfirm = () => {
    act(() => api.post(`/api/payments/${paymentId}/confirm`, {}));
    setConfirmOpen(false);
  };
  const onCancel = () => {
    act(() => api.post(`/api/payments/${paymentId}/cancel`, { reason: cancelReason || '' }));
    setCancelOpen(false);
    setCancelReason('');
  };
  const onDestroy = () => {
    act(async () => { await api.delete('/api/payments/' + paymentId); onClose(); });
    setDestroyOpen(false);
  };
  const onAddItem = () => {
    const price = parseFloat((addPrice || '0').replace(',', '.'));
    const qty   = parseFloat((addQty   || '1').replace(',', '.'));
    if (!isFinite(price) || price <= 0) { setError('Укажите положительную цену'); return; }
    if (!isFinite(qty)   || qty   <= 0) { setError('Укажите положительное количество'); return; }
    if (!addName.trim()) { setError('Укажите название позиции'); return; }
    act(() => api.post(`/api/payments/${paymentId}/items`, {
      kind: addKind, name: addName.trim(),
      unit_price: price, quantity: qty, amount: +(price * qty).toFixed(2),
    }));
    setAddOpen(false);
    setAddName(''); setAddPrice(''); setAddQty('1'); setAddKind('manual');
  };
  const onRemoveItem = (itemId) => {
    act(() => api.delete(`/api/payments/${paymentId}/items/${itemId}`));
  };

  // ЮКасса: создать платёж в провайдере. После успеха карточка перезагрузится
  // и покажет confirmation_url (для карты) или confirmation_qr (для СБП).
  // Webhook позже переведёт статус в succeeded — карточка обновится через WS.
  const onCharge = (method) => {
    act(async () => {
      const res = await api.post(`/api/payments/${paymentId}/charge`, { method });
      if (res?.confirmationUrl) {
        // Карта — открываем ЮКассу в новой вкладке.
        window.open(res.confirmationUrl, '_blank', 'noopener');
      }
      // Для qr — пользователь увидит блок с confirmation_qr на самой карточке.
    });
  };

  // Ручная (re)отправка платежа в 1С. Авто-триггер из webhook'а / confirm
  // мог упасть (1С недоступна) — этой кнопкой добивается. Node пингуется,
  // а через WS payment.updated карточка обновится сама.
  const onSendToOnec = () => act(() => api.post(`/api/payments/${paymentId}/send-to-onec`, {}));

  // «Обновить чек» — для succeeded modulkassa/yookassa/bank_transfer.
  // Node дёргает то же самое каждые 15 мин, но кнопка позволяет операторy
  // потянуть чек сразу после оплаты, не ждать.
  const onRefreshReceipt = () =>
    act(() => api.post(`/api/payments/${paymentId}/refresh-receipt`, {}));

  // «Выдать чек» — для банковского перевода. Открывает inline-форму,
  // где вводится email/телефон покупателя. По submit — POST issue-receipt.
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptContact, setReceiptContact] = useState('');
  const onIssueReceipt = () => {
    const c = receiptContact.trim();
    if (!c) { setError('Введите email или телефон покупателя'); return; }
    act(() => api.post(`/api/payments/${paymentId}/issue-receipt`, { contact: c }));
    setReceiptOpen(false);
    setReceiptContact('');
  };

  // Открытие/закрытие inline-формы «Сменить плательщика/договор».
  // При открытии префиксуем выбранного ученика из текущего платежа, чтобы
  // оператор сразу увидел доступные договоры — типичный сценарий «тот же
  // ребёнок, не тот договор».
  const onRebindOpen = () => {
    if (!p) return;
    // Имя ученика догрузит сам StudentFilterChip по id — payment.show
    // отдаёт только studentId, дополнительные поля не нужны.
    setRebindStudent({ id: p.studentId });
    setRebindAgreementId(p.agreementId ? String(p.agreementId) : '');
    setRebindOpen(true);
  };
  const onRebindCancel = () => {
    setRebindOpen(false);
    setRebindStudent(null);
    setRebindAgreementId('');
    setRebindAgreements([]);
  };

  // При смене выбранного ученика — догружаем его список договоров и
  // сбрасываем выбранный, если он не из нового набора.
  useEffect(() => {
    if (!rebindOpen || !rebindStudent?.id) { setRebindAgreements([]); return; }
    let cancelled = false;
    setRebindLoading(true);
    api.get(`/api/payments/context?student_id=${rebindStudent.id}`)
      .then((res) => {
        if (cancelled) return;
        const list = (res.agreements || []);
        setRebindAgreements(list);
        if (rebindAgreementId && !list.some((a) => String(a.id) === String(rebindAgreementId))) {
          setRebindAgreementId('');
        }
      })
      .catch(() => { if (!cancelled) setRebindAgreements([]); })
      .finally(() => { if (!cancelled) setRebindLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebindOpen, rebindStudent?.id]);

  const onRebindSave = () => {
    if (!p) return;
    const newStudentId   = rebindStudent?.id ?? null;
    const newAgreementId = rebindAgreementId ? Number(rebindAgreementId) : null;
    // Бэк ругнётся, если не передан ни один из них — продублируем здесь
    // для понятного сообщения.
    const studentChanged   = newStudentId   && newStudentId   !== p.studentId;
    const agreementChanged = (newAgreementId || null) !== (p.agreementId || null);
    if (!studentChanged && !agreementChanged) {
      setError('Изменения не выбраны: укажите другого ученика или договор');
      return;
    }
    act(() => api.post(`/api/payments/${paymentId}/onec-rebind`, {
      studentId:   studentChanged   ? newStudentId   : undefined,
      agreementId: agreementChanged ? newAgreementId : undefined,
    }));
    onRebindCancel();
  };

  const p = data?.payment;
  const isDraft  = p?.status === 'draft';
  const isManual = p && ['manual','bank_transfer','cash','terminal'].includes(p.provider);
  // Для bank_transfer/корректировок перенос — только в CRM (их документ
  // живёт на стороне 1С, PUT туда не шлём).
  const isLocalRebind = p && ['bank_transfer', 'onec_correction'].includes(p.provider);
  // Перекомпоновка позиций проведённого платежа (разбить взнос на
  // «вступительный» + «в счёт ежемесячной оплаты», поправить раскладку).
  // Доступна для любого провайдера: в 1С позиции не передаются, а чек ОФД
  // не перевыпускается (осознанный компромисс). Сумма платежа фиксирована —
  // меняется только раскладка по позициям.
  const canRecompose = p?.status === 'succeeded' && canReassign;
  const itemsSum = (data?.items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const itemsMismatch = p && p.status !== 'draft'
    && Math.abs(itemsSum - (Number(p.amount) || 0)) > 0.005;
  const statusInfo = p ? (STATUS_LABELS[p.status] || { label: p.status, cls: 'pay-st' }) : null;

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !busy) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>
            Платёж #{paymentId}
            {p && (
              <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--fg-muted)', fontWeight: 400 }}>
                {fmtAmount(p.amount)} · {PROVIDER_LABELS[p.provider] || p.provider}
              </span>
            )}
          </h2>
          <button className="sd-close" onClick={onClose} disabled={busy}><Icon name="x" size={14} /></button>
        </div>

        <div className="sd-edit__body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="sd-edit__error">{error}</div>}
          {!data && !error && <div className="ref-empty">Загрузка…</div>}

          {data && (
            <>
              {/* Краткая сводка */}
              <div className="pay-summary">
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Статус</span>
                  <span className={statusInfo.cls}>{statusInfo.label}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Создан</span>
                  <span>{fmtDate(p.createdAt)}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Ученик</span>
                  <span>ID {p.studentId}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Договор</span>
                  <span>
                    {p.agreement
                      ? <>
                          № {p.agreement.displayNumber || p.agreement.number || p.agreement.id}
                          {p.agreement.date && <> от {fmtDateRuShort(p.agreement.date)}</>}
                          <span className="muted"> (ID {p.agreement.id})</span>
                        </>
                      : (p.agreementId ? <>ID {p.agreementId}</> : '—')}
                  </span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Плательщик</span>
                  <span>{p.payerName || '—'}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Телефон</span>
                  <span className="mono">{p.payerPhone || '—'}</span>
                </div>
                {p.description && (
                  <div className="pay-summary__row pay-summary__row--full">
                    <span className="pay-summary__label">Описание</span>
                    <span>{p.description}</span>
                  </div>
                )}
                {p.paidAt && (
                  <div className="pay-summary__row">
                    <span className="pay-summary__label">Оплачен</span>
                    <span>{fmtDate(p.paidAt)}</span>
                  </div>
                )}
                {p.onecDocumentId && (
                  <div className="pay-summary__row">
                    <span className="pay-summary__label">Документ 1С</span>
                    <span className="mono">{p.onecDocumentId}</span>
                  </div>
                )}
                {p.onecSentAt && (
                  <div className="pay-summary__row">
                    <span className="pay-summary__label">Отправлен в 1С</span>
                    <span>{fmtDate(p.onecSentAt)}</span>
                  </div>
                )}
              </div>

              {/* Действия */}
              <div className="pay-actions">
                {canWrite && isDraft && isManual && !confirmOpen && (
                  <button className="btn btn--primary btn--sm" disabled={busy} onClick={() => setConfirmOpen(true)}>
                    <Icon name="check" size={12} /> Отметить оплаченным
                  </button>
                )}
                {/* ЮКасса принимает только СБП — карточный эквайринг через ЮКассу
                    мы намеренно не используем (комиссия выше). Если нужен платёж
                    картой — это сценарий через Модулькассу в офисе. */}
                {canWrite && isDraft && p.provider === 'yookassa' && (
                  <button className="btn btn--primary btn--sm" disabled={busy} onClick={() => onCharge('qr')}>
                    <Icon name="qr" size={12} /> Оплатить (СБП)
                  </button>
                )}
                {/* Модулькасса: отправляем заказ на кассу, кассир пробивает,
                    тип расчёта (нал/карта) определяется в момент оплаты. */}
                {canWrite && isDraft && p.provider === 'modulkassa' && (
                  <button className="btn btn--primary btn--sm" disabled={busy} onClick={() => onCharge('qr')}>
                    <Icon name="check" size={12} /> Отправить на кассу
                  </button>
                )}
                {canWrite && p.status === 'pending' && p.provider === 'yookassa' && p.confirmationUrl && (
                  <a className="btn btn--sm" href={p.confirmationUrl} target="_blank" rel="noopener noreferrer">
                    <Icon name="link" size={12} /> Открыть страницу оплаты
                  </a>
                )}
                {/* Кнопка ручной отправки в 1С: видна только если платёж succeeded,
                    провайдер «биллабельный» (не bank_transfer / не onec_correction),
                    и в 1С его ещё не отправляли. */}
                {canWrite && p.status === 'succeeded' && !p.onecDocumentId
                  && !['bank_transfer', 'onec_correction'].includes(p.provider) && (
                  <button className="btn btn--sm" disabled={busy} onClick={onSendToOnec}>
                    <Icon name="upload" size={12} /> Отправить в 1С
                  </button>
                )}
                {/* Чеки ОФД. Три сценария:
                    • bank_transfer succeeded без receipt — «Выдать чек»
                      (Модулькасса FN v2/doc). Открывает inline-форму
                      с полем email/телефон покупателя.
                    • modulkassa/yookassa succeeded без receipt — «Обновить чек»
                      (тянет ссылку из готового чека провайдера). Работает и
                      для bank_transfer, если чек уже выдан ранее.
                    • receipt есть — кнопка «Открыть чек» ведёт на 1-OFD. */}
                {/* Чек уже в очереди на печать, если по нему есть событие
                    `receipt_queued` без последующего `receipt_fetched`. Кнопку
                    «Выдать» скрываем — вместо неё остаётся только «Обновить»,
                    которая опрашивает FN status Модулькассы. Ссылка появится
                    сама через 5–15 минут (webhook или следующий cron-тик). */}
                {(() => {
                  const hasQueued  = (data.events || []).some((e) => e.event === 'receipt_queued');
                  const hasFetched = (data.events || []).some((e) => e.event === 'receipt_fetched');
                  const receiptPending = hasQueued && !hasFetched && !p.receiptUrl;
                  return (
                    <>
                      {canWrite && p.status === 'succeeded' && !p.receiptUrl && !receiptPending
                        && p.provider === 'bank_transfer' && !receiptOpen && (
                        <button className="btn btn--sm" disabled={busy}
                                onClick={() => {
                                  // Префилл: сначала email подписанта, затем телефон
                                  // (54-ФЗ требует email ИЛИ телефон). Если админ уже
                                  // сохранил payer_email/phone на карточке — предпочтем
                                  // их (значит поле специально уточняли).
                                  const prefill = p.payerEmail
                                    || p.payerPhone
                                    || p.signatory?.email
                                    || p.signatory?.phone
                                    || '';
                                  setReceiptContact(prefill);
                                  setReceiptOpen(true);
                                }}>
                          <Icon name="doc" size={12} /> Выдать чек
                        </button>
                      )}
                      {receiptPending && (
                        <span className="pay-st pay-st--pending" style={{ alignSelf: 'center' }}>
                          Чек в очереди на печать
                        </span>
                      )}
                      {canWrite && p.status === 'succeeded' && !p.receiptUrl
                        && ['modulkassa', 'yookassa', 'bank_transfer'].includes(p.provider) && (
                        <button className="btn btn--sm" disabled={busy} onClick={onRefreshReceipt}>
                          <Icon name="download" size={12} /> Обновить чек
                        </button>
                      )}
                    </>
                  );
                })()}
                {p.receiptUrl && (
                  <a className="btn btn--sm" href={p.receiptUrl}
                     target="_blank" rel="noopener noreferrer">
                    <Icon name="external" size={12} /> Открыть чек ОФД
                  </a>
                )}
                {/* Сменить плательщика/договор — кнопка для уже проведённых
                    платежей. Это «починка задним числом» когда касса/админ
                    выбрал не того ребёнка или не тот договор, либо платёж
                    (например, взнос) переносится с аннулированного договора.
                    Право отдельное — payments.reassign. */}
                {canReassign && !rebindOpen
                  && ['succeeded', 'refunded', 'partially_refunded'].includes(p.status) && (
                  <button className="btn btn--sm" disabled={busy} onClick={onRebindOpen}>
                    Сменить плательщика/договор
                  </button>
                )}
                {canWrite && !cancelOpen && (
                  // Отмена доступна для draft/pending всегда. Для succeeded —
                  // только когда это «Корректировка 1С» (служебная проводка
                  // без фискалки). Бэк дополнительно проверит.
                  (p.status === 'draft' || p.status === 'pending'
                    || (p.status === 'succeeded' && p.provider === 'onec_correction')) && (
                    <button className="btn btn--sm" disabled={busy} onClick={() => setCancelOpen(true)}>
                      {p.provider === 'onec_correction' && p.status === 'succeeded'
                        ? 'Отменить корректировку'
                        : 'Отменить'}
                    </button>
                  )
                )}
                {canDelete && isDraft && !destroyOpen && (
                  <button className="btn btn--sm btn--danger" disabled={busy} onClick={() => setDestroyOpen(true)}>
                    Удалить черновик
                  </button>
                )}
                {/* Удаление проведённой «Корректировки 1С» — по отдельному
                    праву payments.corrections/delete (Настройки → права).
                    Служебная проводка в 1С не уходит, поэтому её можно снести
                    целиком (в отличие от обычных оплаченных платежей). */}
                {canDeleteCorrection && p.provider === 'onec_correction' && !isDraft && !destroyOpen && (
                  <button className="btn btn--sm btn--danger" disabled={busy} onClick={() => setDestroyOpen(true)}>
                    Удалить корректировку
                  </button>
                )}
              </div>

              {/* Inline-форма «Выдать чек» — email/телефон покупателя.
                  Показывается после клика на «Выдать чек» для bank_transfer. */}
              {receiptOpen && (
                <div className="pay-inline pay-inline--ok">
                  <div className="pay-inline__title">Выдать чек ОФД по банковскому переводу</div>
                  <div className="pay-inline__hint">
                    По 54-ФЗ покупателю нужно отправить чек. Введите email или телефон
                    получателя — Модулькасса напечатает чек и вышлет.
                  </div>
                  <div className="pay-inline__actions" style={{ gap: 6 }}>
                    <input
                      className="sd-edit__input"
                      style={{ flex: 1, minWidth: 220 }}
                      placeholder="email@example.com или +79001234567"
                      value={receiptContact}
                      onChange={(e) => setReceiptContact(e.target.value)}
                      autoFocus
                    />
                    <button className="btn btn--sm" onClick={() => { setReceiptOpen(false); setReceiptContact(''); }}
                            disabled={busy}>Отмена</button>
                    <button className="btn btn--primary btn--sm" onClick={onIssueReceipt} disabled={busy}>
                      {busy ? 'Отправляем…' : 'Выдать чек'}
                    </button>
                  </div>
                </div>
              )}

              {/* Модулькасса: pending = заказ висит на кассе, ждём кассира. */}
              {p.status === 'pending' && p.provider === 'modulkassa' && (
                <div className="pay-inline">
                  <div className="pay-inline__title">Ожидаем оплаты на кассе</div>
                  <div className="pay-inline__hint">
                    Заказ #{p.externalId} отправлен на кассу. Кассир пробьёт оплату — статус обновится автоматически.
                  </div>
                </div>
              )}

              {/* Конфирмация ЮКассы — оплата по СБП. Покажем QR-код, ссылку
                  и три кнопки шаринга (WhatsApp / Telegram / Email). Webhook
                  переведёт в succeeded — карточка обновится через WS. */}
              {p.status === 'pending' && p.provider === 'yookassa' && p.confirmationQr && (
                <SbpQrBlock url={p.confirmationQr} />
              )}

              {/* Inline-подтверждение «Оплачено» */}
              {confirmOpen && (
                <div className="pay-inline pay-inline--ok">
                  <div className="pay-inline__title">Отметить платёж оплаченным?</div>
                  <div className="pay-inline__hint">
                    Сумма {fmtAmount(p.amount)}, канал «{PROVIDER_LABELS[p.provider] || p.provider}». Действие записывается в историю.
                  </div>
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => setConfirmOpen(false)} disabled={busy}>Отмена</button>
                    <button className="btn btn--primary btn--sm" onClick={onConfirm} disabled={busy}>
                      {busy ? 'Сохраняем…' : 'Подтвердить'}
                    </button>
                  </div>
                </div>
              )}

              {/* Inline-форма отмены */}
              {cancelOpen && (
                <div className="pay-inline">
                  <div className="pay-inline__title">Отменить платёж</div>
                  <input
                    className="sd-edit__input"
                    placeholder="Причина (необязательно)"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    autoFocus
                  />
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => { setCancelOpen(false); setCancelReason(''); }} disabled={busy}>Не отменять</button>
                    <button className="btn btn--sm btn--danger" onClick={onCancel} disabled={busy}>
                      {busy ? 'Отменяем…' : 'Отменить платёж'}
                    </button>
                  </div>
                </div>
              )}

              {/* Inline-форма смены плательщика/договора */}
              {rebindOpen && (
                <div className="pay-inline">
                  <div className="pay-inline__title">Сменить плательщика и/или договор</div>
                  <div className="pay-inline__hint">
                    {isLocalRebind ? (
                      <>
                        Перенос затронет <b>только CRM</b>: документ в 1С останется на
                        прежнем контрагенте/договоре — при необходимости бухгалтер
                        переносит его в 1С вручную.
                        {p.onecDocumentNumber && (
                          <> Документ 1С: <b>{p.onecDocumentNumber}</b>.</>
                        )}
                      </>
                    ) : (
                      <>
                        Изменения уйдут в 1С через PUT — документ оплаты и связанные счета на
                        оплату будут перепривязаны на нового контрагента и/или новый договор.
                        Чек ОФД не перевыпускается; для исправления чека это операция не подходит.
                        {p.onecDocumentNumber && (
                          <> Сейчас в 1С: документ № <b>{p.onecDocumentNumber}</b>.</>
                        )}
                      </>
                    )}
                  </div>

                  <div className="pay-add__grid" style={{ marginTop: 6 }}>
                    <div className="sd-edit__field sd-edit__field--full">
                      <label className="sd-edit__label">Плательщик (ученик → контрагент 1С)</label>
                      <StudentFilterChip
                        studentId={rebindStudent?.id || ''}
                        onChange={(s) => {
                          setRebindStudent(s);
                          // Сменили ученика — старый agreementId почти
                          // наверняка не от него. Сбросим и заставим
                          // выбрать заново.
                          if (s?.id !== rebindStudent?.id) setRebindAgreementId('');
                        }}
                      />
                    </div>
                    <div className="sd-edit__field sd-edit__field--full">
                      <label className="sd-edit__label">Договор</label>
                      <select
                        className="sd-edit__select"
                        value={rebindAgreementId}
                        onChange={(e) => setRebindAgreementId(e.target.value)}
                        disabled={rebindLoading || !rebindStudent?.id}
                      >
                        <option value="">— оставить как есть —</option>
                        {rebindAgreements.map((a) => (
                          <option
                            key={a.id}
                            value={a.id}
                            // При переносе с PUT в 1С договор обязан быть
                            // привязан к 1С; при локальном (bank_transfer /
                            // корректировка) — ограничения нет.
                            disabled={!isLocalRebind && !a.onecLinked}
                            title={!isLocalRebind && !a.onecLinked ? 'Договор не привязан к 1С' : undefined}
                          >
                            {a.displayNumber || ('№ ' + a.number)}
                            {a.date ? ' от ' + fmtDateRuShort(a.date) : ''}
                            {a.type === 'school' ? ' · школа' : a.type === 'kindergarten' ? ' · сад' : a.type === 'extras' ? ' · доп.' : ''}
                            {!a.onecLinked ? ' (нет в 1С)' : ''}
                          </option>
                        ))}
                      </select>
                      {rebindLoading && (
                        <div className="sd-edit__hint">Загружаем договоры…</div>
                      )}
                    </div>
                  </div>

                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={onRebindCancel} disabled={busy}>
                      Отмена
                    </button>
                    <button
                      className="btn btn--primary btn--sm"
                      onClick={onRebindSave}
                      disabled={busy || rebindLoading}
                    >
                      {busy ? 'Сохраняем…' : 'Применить'}
                    </button>
                  </div>
                </div>
              )}

              {/* Inline-подтверждение удаления */}
              {destroyOpen && (
                <div className="pay-inline pay-inline--danger">
                  <div className="pay-inline__title">
                    {p.provider === 'onec_correction' && p.status !== 'draft'
                      ? 'Удалить корректировку 1С?'
                      : 'Удалить черновик?'}
                  </div>
                  <div className="pay-inline__hint">Действие необратимо. История будет удалена вместе с платежом.</div>
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => setDestroyOpen(false)} disabled={busy}>Не удалять</button>
                    <button className="btn btn--sm btn--danger" onClick={onDestroy} disabled={busy}>
                      {busy ? 'Удаляем…' : 'Удалить'}
                    </button>
                  </div>
                </div>
              )}

              {/* Позиции */}
              <div className="pay-section">
                <div className="pay-section__head">
                  <h4>Позиции ({data.items.length})</h4>
                  {((canWrite && isDraft) || canRecompose) && !addOpen && (
                    <button className="btn btn--sm" onClick={() => setAddOpen(true)} disabled={busy}>
                      <Icon name="plus" size={12} /> Добавить
                    </button>
                  )}
                </div>

                {/* Авто-сверка с кассой: кассир изменил состав заказа на
                    терминале, CRM привела платёж к фактическому чеку. */}
                {(() => {
                  const rec = (data.events || []).find((e) => e.event === 'provider_reconciled');
                  if (!rec || !rec.payload) return null;
                  const pl = rec.payload;
                  return (
                    <div style={{
                      border: '1px solid var(--amber-fg, #b45f00)', borderRadius: 6,
                      padding: '8px 10px', marginBottom: 8, fontSize: 12.5,
                      color: 'var(--amber-fg, #b45f00)',
                    }}>
                      Касса пробила {fmtAmount(pl.actualAmount)} вместо выставленных{' '}
                      {fmtAmount(pl.expectedAmount)} — состав заказа изменили на терминале.
                      {pl.itemsSynced
                        ? ' Платёж автоматически приведён к чеку: сумма и позиции.'
                        : ' Сумма платежа приведена к чеку автоматически — проверьте позиции.'}
                    </div>
                  );
                })()}

                {/* Контроль перекомпоновки: отчёты считают оплату по позициям,
                    поэтому их сумма обязана сходиться с суммой платежа. */}
                {itemsMismatch && (
                  <div className="sd-edit__error" style={{ marginBottom: 8 }}>
                    Сумма позиций ({fmtAmount(itemsSum)}) не сходится с суммой платежа
                    ({fmtAmount(p.amount)}) — разница {fmtAmount(Math.abs(itemsSum - p.amount))}.
                    {canRecompose
                      ? ' Добавьте или поправьте позиции, чтобы раскладка совпала с платежом.'
                      : ' Обратитесь к администратору с правом переноса платежей.'}
                  </div>
                )}
                {canRecompose && addOpen && (
                  <div className="sd-edit__hint" style={{ marginBottom: 6 }}>
                    Платёж проведён — его сумма не изменится. Позиции задают
                    раскладку: например, «Сад · вступительный» + «Сад · ежемесячно»
                    в сумме должны дать {fmtAmount(p.amount)}.{' '}
                    {isLocalRebind ? (
                      <>
                        Документ в 1С создан на её стороне (выписка/корректировка) —
                        разнесение там при необходимости правит бухгалтер вручную.
                      </>
                    ) : p.onecDocumentId ? (
                      <>
                        Как только суммы сойдутся, CRM автоматически переотправит
                        раскладку в 1С — документ{p.onecDocumentNumber ? <> № <b>{p.onecDocumentNumber}</b></> : null} будет
                        перепроведён с новым разнесением по статьям и счётом.
                      </>
                    ) : (
                      <>В 1С платёж ещё не отправлен — при отправке уйдёт уже новая раскладка.</>
                    )}{' '}
                    Чек ОФД <b>не перевыпускается</b>.
                  </div>
                )}

                {addOpen && (
                  <div className="pay-add">
                    <div className="pay-add__grid">
                      <div className="sd-edit__field sd-edit__field--full">
                        <label className="sd-edit__label">Тип</label>
                        <select className="sd-edit__select" value={addKind} onChange={(e) => setAddKind(e.target.value)}>
                          {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                      <div className="sd-edit__field sd-edit__field--full">
                        <label className="sd-edit__label">Название позиции *</label>
                        <input
                          className="sd-edit__input"
                          value={addName}
                          onChange={(e) => setAddName(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="sd-edit__field">
                        <label className="sd-edit__label">Цена за ед., ₽ *</label>
                        <input
                          className="sd-edit__input"
                          type="number" step="0.01" min="0"
                          value={addPrice}
                          onChange={(e) => setAddPrice(e.target.value)}
                        />
                      </div>
                      <div className="sd-edit__field">
                        <label className="sd-edit__label">Количество *</label>
                        <input
                          className="sd-edit__input"
                          type="number" step="1" min="0"
                          value={addQty}
                          onChange={(e) => setAddQty(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="pay-inline__actions">
                      <button className="btn btn--sm" onClick={() => setAddOpen(false)} disabled={busy}>Отмена</button>
                      <button className="btn btn--primary btn--sm" onClick={onAddItem} disabled={busy}>
                        {busy ? 'Добавляем…' : 'Добавить'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="table-wrap">
                  <table className="table" style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Тип</th>
                        <th>Название</th>
                        <th style={{ width: 100, textAlign: 'right' }}>Цена</th>
                        <th style={{ width: 70, textAlign: 'right' }}>Кол-во</th>
                        <th style={{ width: 110, textAlign: 'right' }}>Сумма</th>
                        {((isDraft && canWrite) || canRecompose) && <th style={{ width: 36 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((it) => (
                        <tr key={it.id}>
                          <td className="muted">{KIND_LABELS[it.kind] || it.kind}</td>
                          <td>{it.name}</td>
                          <td className="mono" style={{ textAlign: 'right' }}>{fmtAmount(it.unitPrice)}</td>
                          <td className="mono" style={{ textAlign: 'right' }}>{it.quantity}</td>
                          <td className="mono" style={{ textAlign: 'right' }}><b>{fmtAmount(it.amount)}</b></td>
                          {((isDraft && canWrite) || canRecompose) && (
                            <td>
                              <button
                                className="agr-scan__iconbtn agr-scan__iconbtn--danger"
                                onClick={() => onRemoveItem(it.id)}
                                title="Удалить позицию"
                                disabled={busy}
                              >
                                <Icon name="x" size={11} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {data.items.length === 0 && (
                        <tr>
                          <td colSpan={isDraft && canWrite ? 6 : 5}
                              style={{ padding: 12, textAlign: 'center', color: 'var(--fg-muted)' }}>
                            Позиций нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Превью чека ОФД — как позиции свернутся в фискальный чек.
                  Все доп. занятия объединяются в одну общую строку. Названия
                  и НДС берутся из «Настройки → Чеки». */}
              {Array.isArray(data.receiptItems) && data.receiptItems.length > 0 && (
                <div className="pay-section">
                  <div className="pay-section__head">
                    <h4>Чек ОФД (предпросмотр)</h4>
                  </div>
                  <div className="table-wrap">
                    <table className="table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Позиция</th>
                          <th style={{ width: 80, textAlign: 'right' }}>Кол-во</th>
                          <th style={{ width: 120, textAlign: 'right' }}>Сумма</th>
                          <th style={{ width: 70, textAlign: 'right' }}>НДС</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.receiptItems.map((r, i) => (
                          <tr key={i}>
                            <td>
                              {r.name}
                              {r.sources && r.sources.length > 1 && (
                                <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                                  (объединено: {r.sources.length})
                                </span>
                              )}
                            </td>
                            <td className="mono" style={{ textAlign: 'right' }}>{r.quantity}</td>
                            <td className="mono" style={{ textAlign: 'right' }}>
                              {Number(r.amount).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                            </td>
                            <td className="mono" style={{ textAlign: 'right' }}>
                              {r.vat_rate === 0 ? 'без НДС' : r.vat_rate + '%'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* События */}
              <div className="pay-section">
                <div className="pay-section__head">
                  <h4>История ({data.events.length})</h4>
                </div>
                <div className="table-wrap">
                  <table className="table" style={{ fontSize: 11 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 130 }}>Время</th>
                        <th style={{ width: 150 }}>Событие</th>
                        <th>Подробности</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.events.map((e) => (
                        <tr key={e.id}>
                          <td className="mono">{fmtDate(e.createdAt)}</td>
                          <td className="muted">{e.event}</td>
                          <td className="muted" style={{ fontFamily: 'var(--font-mono)' }}>
                            {e.payload ? JSON.stringify(e.payload) : ''}
                          </td>
                        </tr>
                      ))}
                      {data.events.length === 0 && (
                        <tr>
                          <td colSpan={3} style={{ padding: 12, textAlign: 'center', color: 'var(--fg-muted)' }}>
                            Событий пока нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose} disabled={busy}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Блок СБП-оплаты: QR-код + ссылка + кнопки шаринга                       */
/* ----------------------------------------------------------------------- */
function SbpQrBlock({ url }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Если clipboard API недоступно (старый браузер / non-https) —
      // тихо игнорируем; ссылка и так видна.
    }
  };

  // Шаринг работает аналогично старой версии: открываем платформенный
  // share-URL в новой вкладке. Текст «Ваша ссылка на оплату: …» — как был.
  const text = 'Ваша ссылка на оплату: ' + url;
  const shareWhatsapp = () =>
    window.open('https://api.whatsapp.com/send?text=' + encodeURIComponent(text), '_blank', 'noopener');
  const shareTelegram = () =>
    window.open('https://t.me/share/url?url=' + encodeURIComponent(text), '_blank', 'noopener');
  const shareEmail = () => {
    const subject = encodeURIComponent('Ссылка на оплату');
    const body    = encodeURIComponent(text);
    window.open('mailto:?subject=' + subject + '&body=' + body, '_blank', 'noopener');
  };
  // Native Web Share API — системный диалог «поделиться». Если на устройстве
  // установлены Max / WhatsApp / Telegram / другой мессенджер — они появятся
  // в списке. Работает на мобильных и в Chrome/Edge на HTTPS-десктопе.
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const shareNative = async () => {
    try {
      await navigator.share({ title: 'Ссылка на оплату', text, url });
    } catch { /* пользователь отменил — игнорируем */ }
  };

  return (
    <div className="pay-qr">
      <div className="pay-qr__title">Оплата по СБП</div>
      <div className="pay-qr__hint">
        Отсканируйте QR-код в приложении мобильного банка с поддержкой СБП —
        или отправьте плательщику ссылку. Статус обновится автоматически,
        когда оплата пройдёт.
      </div>

      <div className="pay-qr__main">
        <div className="pay-qr__img">
          <QRCodeSVG value={url} size={220} level="M" includeMargin={false} />
        </div>

        <div className="pay-qr__side">
          <div className="pay-qr__url-row">
            <input className="sd-edit__input mono" readOnly value={url} onFocus={(e) => e.target.select()} />
            <button type="button" className="btn btn--sm" onClick={onCopy} title="Скопировать ссылку">
              <Icon name={copied ? 'check' : 'doc'} size={12} /> {copied ? 'Скопировано' : 'Копировать'}
            </button>
          </div>

          <div className="pay-qr__share">
            <button type="button" className="btn btn--sm"
                    style={{ background: '#25D366', color: '#fff', borderColor: '#25D366' }}
                    onClick={shareWhatsapp}>
              WhatsApp
            </button>
            <button type="button" className="btn btn--sm"
                    style={{ background: '#229ED9', color: '#fff', borderColor: '#229ED9' }}
                    onClick={shareTelegram}>
              Telegram
            </button>
            <button type="button" className="btn btn--sm" onClick={shareEmail}>
              <Icon name="mail" size={12} /> Email
            </button>
            {canNativeShare && (
              <button type="button" className="btn btn--sm" onClick={shareNative}
                      title="Системный диалог: выбрать любое приложение из установленных">
                <Icon name="share" size={12} /> Поделиться…
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
